import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './contexts/AuthContext';
import { AuthContextWrapper } from './contexts/AuthContextWrapper';
import Auth0ProviderWithNavigate from './contexts/Auth0Provider';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import OnboardingComplete from './pages/OnboardingComplete';
import Dashboard from './pages/Dashboard';
import Quests from './pages/Quests';
import Leagues from './pages/Leagues';
import Feed from './pages/Feed';
import Profile from './pages/Profile';
import FarmManagement from './pages/FarmManagement';
import AddPlant from './pages/AddPlant';

// Components
import OfflineIndicator from './components/OfflineIndicator';
import SyncStatus from './components/SyncStatus';
import InstallPWA from './components/InstallPWA';
import BottomNav from './components/BottomNavigation';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#22c55e', // green-500
      light: '#4ade80',
      dark: '#16a34a',
    },
    secondary: {
      main: '#06b6d4', // cyan-500
      light: '#22d3ee',
      dark: '#0891b2',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
});

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Onboarding Route - redirects if onboarding is complete
const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (user?.onboarding?.completed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingRoute>
              <Onboarding />
            </OnboardingRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/complete"
        element={
          <ProtectedRoute>
            <OnboardingComplete />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quests"
        element={
          <ProtectedRoute>
            <Quests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leagues"
        element={
          <ProtectedRoute>
            <Leagues />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <Feed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/farms/:farmId/manage"
        element={
          <ProtectedRoute>
            <FarmManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/farms/:farmId/plants/add"
        element={
          <ProtectedRoute>
            <AddPlant />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const isAuth0Configured = process.env.REACT_APP_AUTH0_DOMAIN && 
    process.env.REACT_APP_AUTH0_DOMAIN !== 'your-domain.auth0.com';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          {isAuth0Configured ? (
            <Auth0ProviderWithNavigate>
              <AuthContextWrapper>
                <AppContent />
              </AuthContextWrapper>
            </Auth0ProviderWithNavigate>
          ) : (
            <AuthContextWrapper>
              <AppContent />
            </AuthContextWrapper>
          )}
          <OfflineIndicator />
          <div className="fixed top-4 right-4 z-50">
            <SyncStatus />
          </div>
          <BottomNav />
          <InstallPWA />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;